if (!customElements.get('product-info')) {
  class ProductPurchasePlan {

    static storageKey = 'product-purchase-plan';


    static getAll() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      } catch {
        return {};
      }
    }

    static get(handle) {
      const all = this.getAll();
      if (!(handle in all)) {
        this.set(handle, 'subscription');
        return 'subscription';
      }
      return all[handle];
    }

    static set(handle, type) {
      const all = this.getAll();
      all[handle] = type;
      localStorage.setItem(this.storageKey, JSON.stringify(all));
    }
  }

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      dayNightSwitcherUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.mediaItems = this.querySelectorAll('.product__media-item') || false;
        this.variantTypePicker = this.querySelector('.variant-type-picker');
        this.productHandle = this.dataset.productHandle;
        this.planPicker = this.querySelector('.plan-picker');

        if (this.productHandle && this.planPicker) {
          let urlHasVariant = false;
          try {
            urlHasVariant = new URLSearchParams(window.location.search).has('variant');
          } catch (e) {}

          // URL `variant` is the source of truth for Shopping / Merchant Center
          // landings. Do not restore the default 3-month subscription plan.
          if (!urlHasVariant) {
            const savedType = ProductPurchasePlan.get(this.productHandle);
            const currentType = this.planPicker.dataset.currentType;

            if (savedType !== currentType) {
              setTimeout(() => {
                const variantSelects = this.querySelector('variant-selects');
                variantSelects?.applyPurchaseType?.(savedType, {
                  publishEvent: true,
                  selectFirstVisible: false,
                  toggleVariantTitles: false,
                  forceRerender: true,
                });
              }, 0);
            }
          }
        }

        subscribe(PUB_SUB_EVENTS.purchaseTypeChange, (e) => {
          ProductPurchasePlan.set(this.productHandle, e.type);
        });

        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        setTimeout(() => this.resyncPreselectedVariant(), 0);

        this.dayNightSwitcherUnsubscriber = subscribe(PUB_SUB_EVENTS.dayNightSwitcher, (e) => {
          this.dayAndNightSwitcherInit(e.switcher);
        });

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true, detail: { variant: this.getSelectedVariant(this) } }));
        if (this.variantTypePicker) {
          this.updateMediaVisibility(this.getSelectedVariant(this));
        
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.updateMediaVisibility(event?.data?.variant);
          });
        }
      }

      updateMediaVisibility(event) {
        if (!this.mediaItems || !event?.sku) return;
        const hasMatch = Array.from(this.mediaItems).some(item => item.dataset.skuList?.includes(event.sku));
        if (!hasMatch) return;

        this.mediaItems.forEach(item => {
          const itemSku = item.dataset.skuList;
          if (itemSku?.includes(event.sku)) {
            item.classList.remove('hidden');
          } else {
            item.classList.add('hidden');
          }
        });

        const firstVisibleItem = Array.from(this.mediaItems).find(item => !item.classList.contains('hidden'));
        if (firstVisibleItem) {
          firstVisibleItem.style.width = '100%';
          firstVisibleItem.style.maxWidth = '100%';
        }
      }
      

      resyncPreselectedVariant() {
        if (this.pendingRequestUrl) return;

        const variantSelects = this.querySelector('variant-selects');
        const checkedInput = variantSelects?.querySelector('fieldset input:checked');
        const formIdInput = this.querySelector('product-form input[name="id"]');
        if (!checkedInput || !formIdInput) return;

        const label = variantSelects.querySelector(`label[for="${CSS.escape(checkedInput.id)}"]`);
        const optionInfo = label?.querySelector('.option__info:not(.hidden)');
        const variantId = optionInfo?.dataset.variantId;
        if (!variantId || variantId === formIdInput.value) return;

        variantSelects.queueVariantRefresh?.();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Keep ATC on the clicked variant immediately. The section refetch
        // (prices/media) can take several seconds on this PDP.
        const didSyncId = this.syncVariantIdFromOptionInput(target);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        this.resetProductFormState({ skipLock: didSyncId });

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState({ skipLock = false } = {}) {
        const productForm = this.productForm;
        // Lock ATC until the variant fetch finishes so the form cannot submit
        // a stale id / selling_plan pair (e.g. 30-day variant as a one-timer).
        // Skip when the form id was already synced from the clicked option.
        if (!skipLock) productForm?.lockForVariantChange?.();
        productForm?.handleErrorMessage?.();
      }

      syncVariantIdFromOptionInput(input) {
        if (!input?.id) return false;
        const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(input.id) : input.id;
        const label = this.querySelector(`label[for="${escapedId}"]`);
        const info =
          label?.querySelector('.option__info:not(.hidden)') ||
          label?.querySelector('.option__info');
        const variantId = info?.dataset.variantId;
        if (!variantId) return false;
        this.updateVariantInputs(variantId);
        return true;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            this.pendingRequestUrl = null;
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            }
            console.error(error);
            this.pendingRequestUrl = null;
            this.productForm?.toggleSubmitButton(false);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateOptionsPackageInfo(html) {
        const source = html.querySelectorAll('variant-selects .variant-option_package [data-id]');
        const destination = this.variantSelectors?.querySelectorAll('.variant-option_package [data-id]') || false;

        if (source.length > 0 && destination){
          destination.forEach(curEl  => {
            source.forEach(newEl  => {
              if (curEl.dataset.id == newEl.dataset.id) {
                curEl.innerHTML = newEl.innerHTML;
              }
            });
          });
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, variant);

          // Only use this function for products with few options.
          // Replace product option info
          if (variant?.option2 != null) this.updateOptionsPackageInfo(html);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };


          updateSourceFromDestination('ProductSubmitButton');
          updateSourceFromDestination('BasePrice');
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          if (location.href.indexOf('protein') > -1) {
            updateSourceFromDestination('BuyButtons'); //Temp fix

            const currentType = this.planPicker?.dataset?.currentType || 'subscription';
            const sellingPlanInput = this.querySelector(`#BuyButtons-${this.dataset.section} input[name="selling_plan"]`);
            if (sellingPlanInput) {
              if (currentType === 'onetimer') {
                sellingPlanInput.setAttribute('disabled', 'disabled');
              } else {
                sellingPlanInput.removeAttribute('disabled');
              }
            }
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          // Section render updates price/ATC but not this JSON blob — keep it in sync
          // so campaign scripts read the current variant price (not the first load).
          const selectedVariantNode = this.querySelector('variant-selects [data-selected-variant]');
          if (selectedVariantNode && variant) {
            selectedVariantNode.textContent = JSON.stringify(variant);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId, variant) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));

          const inpuSellingPlan = productForm.querySelector('input[name="selling_plan"]') || false;
          const newPlan = this.getCurrentSellingPlanId(variant);
    
          if (inpuSellingPlan && newPlan) {
            inpuSellingPlan.setAttribute('value', newPlan);
          }

        });
      }

      getCurrentSellingPlanId(variantData) {
        let newSellingPlan;
        let variantSellingPlanAllocations = variantData?.selling_plan_allocations || false;
        let sellingPlanGropsJSON = this.querySelector(`#ProductSellingPlanGroup-${this.dataset.section}`) || false;
        if(!sellingPlanGropsJSON) return false;
        this.productSellingPlanGroups = this.productSellingPlanGroups || JSON.parse(sellingPlanGropsJSON.textContent);

        if (variantSellingPlanAllocations.length > 0 && this.productSellingPlanGroups) {
          for (let variantPlan of variantData.selling_plan_allocations) {
            if (variantPlan.selling_plan_group_id == this.productSellingPlanGroups.id) {
              newSellingPlan = variantPlan.selling_plan_id;
              break;
            }
          }
        }else {
          return false;
        }
    
        return newSellingPlan;
      }

      // updateURL(url, variantId) {
      //   this.querySelector('share-button')?.updateUrl(
      //     `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
      //   );

      //   if (this.dataset.updateUrl === 'false') return;
      //   window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      // }
      // Merge & add vairant ID, instead of tiying up all URL params
      updateURL(url, variantId) {
        const currentUrl = new URL(window.location.href);

        if (variantId) {
          currentUrl.searchParams.set('variant', variantId);
        } else {
          currentUrl.searchParams.delete('variant');
        }

        const newUrl = `${url}${currentUrl.search}`;

        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${newUrl}`
        );

        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState({}, '', newUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // const mediaGallerySource = this.querySelector('media-gallery ul');
        // const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // const refreshSourceData = () => {
        //   if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
        //   const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
        //   const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
        //   const sourceMap = new Map(
        //     mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
        //   );
        //   return [mediaGallerySourceItems, sourceSet, sourceMap];
        // };

        // if (mediaGallerySource && mediaGalleryDestination) {
        //   let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        //   const mediaGalleryDestinationItems = Array.from(
        //     mediaGalleryDestination.querySelectorAll('li[data-media-id]')
        //   );
        //   const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
        //   let shouldRefresh = false;

        //   // add items from new data not present in DOM
        //   for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        //     if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
        //       mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
        //       shouldRefresh = true;
        //     }
        //   }

        //   // remove items from DOM not present in new data
        //   for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        //     if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
        //       mediaGallerySourceItems[i].remove();
        //       shouldRefresh = true;
        //     }
        //   }

        //   // refresh
        //   if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        //   // if media galleries don't match, sort to match new data order
        //   mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        //     const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

        //     if (sourceData && sourceData.index !== destinationIndex) {
        //       mediaGallerySource.insertBefore(
        //         sourceData.item,
        //         mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
        //       );

        //       // refresh source now that it has been modified
        //       [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        //     }
        //   });
        // }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      dayAndNightSwitcherInit(id) {
        this.renderDayAndNightSwitcherBullets(id);
        this.renderDayAndNightSwitcherIngredients(id);
        this.renderDayAndNightSwitcherClass(id);
      }

      renderDayAndNightSwitcherBullets(id) {
        let bullets = this.querySelectorAll('.description-bullet__wrapper');

        this.toggleHiddenClass(bullets, id);
      }

      renderDayAndNightSwitcherIngredients(id) {
        let ingredients = this.querySelectorAll('.ic-wrapper');
        
        this.toggleHiddenClass(ingredients, id);
      }

      renderDayAndNightSwitcherClass(id) {
        const nightItem = this.querySelector('day-night-switcher .night__wrapper');
        if (nightItem) {
          this.classList.toggle('day-night-active', nightItem.dataset.id === id);
          return;
        }

        const switchers = document.querySelectorAll('.day-and-night__switcher-item');
        const isFirst = Array.from(switchers).findIndex(s => s.dataset.id === id) === 0;

        this.classList.toggle('day-night-active', !isFirst);
      }

      toggleHiddenClass(items, id) {
        items.forEach(item => {
          if (item.dataset.id && item.dataset.id == id) {
            if (item.classList.contains('hidden')) item.classList.remove('hidden');
          } else {
            if (!item.classList.contains('hidden')) item.classList.add('hidden');
          }
        });
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
